import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../../utils/platform';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import { API_BASE } from '../../config/api';

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  let payload = null;
  try { payload = await res.json(); } catch (_) {}
  if (!res.ok) {
    const err = new Error(payload?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export function useProfile() {
  // Vague 10 : refetch automatique au switch de restaurant.
  const { currentRestaurantId } = useCurrentRestaurant();
  const [user, setUser] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const cancelRef = useRef(false);

  const getHeaders = useCallback(async () => {
    const token = await storage.get('token');
    const base = token
      ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
    return currentRestaurantId
      ? { ...base, 'X-Restaurant-Id': currentRestaurantId }
      : base;
  }, [currentRestaurantId]);

  const fetchProfile = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const headers = await getHeaders();
      const [dashboard, config, list] = await Promise.all([
        fetchJson(`${API_BASE}/api/dashboard`, { headers }).catch(() => null),
        fetchJson(`${API_BASE}/api/restaurants/config`, { headers }).catch(() => null),
        fetchJson(`${API_BASE}/api/restaurants`, { headers }).catch(() => ({ restaurants: [] })),
      ]);
      if (cancelRef.current) return;
      setUser(dashboard?.user || null);
      // restaurant : on privilégie config (champs détaillés), fallback dashboard
      setRestaurant(config || dashboard?.restaurant || null);
      setRestaurants(Array.isArray(list?.restaurants) ? list.restaurants : []);
    } catch (e) {
      console.error('[useProfile] fetchProfile failed:', e);
      if (!cancelRef.current) setError(e.message || 'Erreur de chargement');
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [getHeaders]);

  const updateProfile = useCallback(async (payload) => {
    setSaving(true);
    try {
      const headers = await getHeaders();
      const res = await fetchJson(`${API_BASE}/api/auth/profile`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload || {}),
      });
      await fetchProfile();
      return res;
    } finally {
      if (!cancelRef.current) setSaving(false);
    }
  }, [getHeaders, fetchProfile]);

  const updateRestaurant = useCallback(async (payload) => {
    setSaving(true);
    try {
      const headers = await getHeaders();
      const res = await fetchJson(`${API_BASE}/api/restaurants/config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload || {}),
      });
      await fetchProfile();
      return res;
    } finally {
      if (!cancelRef.current) setSaving(false);
    }
  }, [getHeaders, fetchProfile]);

  useEffect(() => {
    cancelRef.current = false;
    fetchProfile();
    return () => { cancelRef.current = true; };
  }, [fetchProfile]);

  return {
    user, restaurant, restaurants,
    loading, saving, error,
    refresh: fetchProfile,
    updateProfile, updateRestaurant,
  };
}
